import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../shared/components/asset-header/asset-header.component';
import { AssetFilterComponent, FilterConfig } from '../../shared/components/asset-filter/asset-filter.component';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { toolsetCards } from './toolset-cards.data';

export interface ToolsetCard {
  title: string;
  shortdescription: string;
  description: string;
  category: string;
  license: string;
  githubLink: string;
}

@Component({
  selector: 'app-toolkit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PaginatorModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
    AssetFilterComponent,
  ],
  templateUrl: './toolkit.component.html',
  styleUrl: './toolkit.component.scss',
})
export class ToolkitComponent implements OnInit {
  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Toolset' }
    ],
    title: 'Toolsets',
    description: 'A secure environment to explore, test, and integrate APIs, data services, and platform capabilities. A controlled testing space for partners and developers to build and validate agri-data solutions on MahaAgX.'
  };

  filterConfig: FilterConfig = {
    sections: [
      {
        id: 'concepts',
        title: 'Concepts',
        type: 'checkbox',
        searchPlaceholder: 'Search for categories',
        isExpanded: true,
        icon: '/filters/leaf.svg',
        options: []
      }
    ],
    activeFilterCount: 0
  };

  cards: ToolsetCard[] = toolsetCards;
  searchQuery = '';
  viewMode: 'grid' | 'list' = 'grid';
  isMobileFilterOpen = false;
  filterSearchPlaceholder = 'Search for categories';

  readonly pageSize = 12;
  currentPage = 1;

  ngOnInit(): void {
    this.buildCategoryFilterOptions();
  }

  get displayedCards(): ToolsetCard[] {
    const cards = this.filteredCards;
    const start = (this.currentPage - 1) * this.pageSize;
    return cards.slice(start, start + this.pageSize);
  }

  get shouldShowPagination(): boolean {
    return this.filteredCards.length > this.pageSize;
  }

  onPageChange(event: PaginatorState): void {
    const first = event.first ?? 0;
    const rows = event.rows ?? this.pageSize;
    if (rows <= 0) {
      return;
    }
    this.currentPage = Math.floor(first / rows) + 1;
  }

  private buildCategoryFilterOptions(): void {
    const categories = [...new Set(this.cards.map(c => c.category))].sort();
    const section = this.filterConfig.sections.find(s => s.id === 'concepts');
    if (section) {
      section.options = categories.map(cat => ({
        id: cat,
        label: cat,
        checked: false
      }));
    }
  }

  get selectedCategoryIds(): Set<string> {
    const section = this.filterConfig.sections.find(s => s.id === 'concepts');
    if (!section?.options) return new Set();
    return new Set(
      section.options.filter(o => o.checked).map(o => o.id)
    );
  }

  get filteredCards(): ToolsetCard[] {
    const q = (this.searchQuery || '').trim().toLowerCase();
    const selected = this.selectedCategoryIds;

    return this.cards.filter(card => {
      const matchesSearch = !q ||
        card.title.toLowerCase().includes(q) ||
        (card.shortdescription && card.shortdescription.toLowerCase().includes(q)) ||
        (card.description && card.description.toLowerCase().includes(q)) ||
        card.category.toLowerCase().includes(q) ||
        card.license.toLowerCase().includes(q);
      const matchesCategory = selected.size === 0 || selected.has(card.category);
      return matchesSearch && matchesCategory;
    });
  }

  onFilterChange(config: FilterConfig): void {
    this.filterConfig = config;
    this.currentPage = 1;
  }

  onResetFilters(): void {
    this.filterConfig.sections.forEach(section => {
      if (section.options) {
        section.options.forEach(opt => { opt.checked = false; });
      }
    });
    this.filterConfig.activeFilterCount = 0;
    this.filterConfig = { ...this.filterConfig };
    this.currentPage = 1;
  }

  onSearchChange(): void {
    this.currentPage = 1;
  }

  onViewModeChange(mode: 'grid' | 'list'): void {
    this.viewMode = mode;
  }

  openMobileFilter(): void {
    this.isMobileFilterOpen = true;
  }

  closeMobileFilter(): void {
    this.isMobileFilterOpen = false;
  }
}
